import { useState, useEffect, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { useNavigate } from "react-router-dom";
import useAdminContactStore from "../../../store/adminContactStore";
import excelIcon from "../../../assets/images/SBA/excel_icon.png";
import { exportToExcel } from "../../../shared/utils/exportToExcel";
import settingsIcon from "../../../assets/images/SBA/settings_icons.png";
import { toast } from "react-toastify";
import { AESClientWebsiteDrawer } from "../../../shared/utils/aesClientWebsiteDrawer";
import {
  getContactCustomize,
  saveContactCustomize,
  resetContactMfa,
} from "../service/adminContact_service";
import useAuthStore from "../../../store/authStore";
import ConfirmDialog from "../../../shared/components/ConfirmDialog"; // adjust path if different

import resetMfaIcon from "../../../../src/assets/images/undo.gif";
import viewClientWebsiteIcon from "../../../../src/assets/images/grid_edit.gif";

const PAGE_ID = 4;

const ContactGrid = ({ rowData, loading }) => {
  const [showCustomize, setShowCustomize] = useState(false);
  const [customizeRows, setCustomizeRows] = useState([]);
  const [visibleCols, setVisibleCols] = useState(new Set());
  const [gridQueryId, setGridQueryId] = useState(0);

  const [showResetMfaConfirm, setShowResetMfaConfirm] = useState(false);
  const [pendingResetRow, setPendingResetRow] = useState(null);

  const navigate = useNavigate();
  const setSelectedAdminContact = useAdminContactStore(
    (state) => state.setAdminContact
  );
  const storedContactData = useAdminContactStore(
    (state) => state.adminContact
  );

  const user = useAuthStore((s) => s) || {};
  const userId = Number(user?.user || 0);

  const customizeUserName = useMemo(() => {
    const first = user?.inputUserName || user?.displayUserName || "User";
    return `Default_${first}`;
  }, [user]);

  const nameCellRenderer = (params) => {
    const handleClick = () => {
      setSelectedAdminContact(params.data);
      const webUserId = params.data.webUserId;
      const contactName = params.data.webUserName;
      localStorage.setItem("CN",contactName);
      const webUserIdEncrypted = btoa(webUserId)
      navigate(`/AdminContact/${webUserIdEncrypted}`, {
        state: { webUserIdEncrypted },
      });
    };

    return (
      <span
        style={{ color: "#10045A", cursor: "pointer" }}
        onClick={handleClick}
      >
        {params.value}
      </span>
    );
  };

  const formatDateOnly = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const formatDateTime = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();

    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${month}-${day}-${year} ${hours}:${minutes} ${ampm}`;
  };

  const yesNoFormatter = (params) => {
    if (params.value === null || params.value === undefined) return "";
    return params.value ? "Yes" : "No";
  };

  const handleResetMfaClick = (row) => {
    if (!row?.webUserId) return;
    setPendingResetRow(row);
    setShowResetMfaConfirm(true);
  };

 const handleConfirmResetMfa = async () => {
  if (!pendingResetRow?.webUserId) {
    setShowResetMfaConfirm(false);
    setPendingResetRow(null);
    return;
  }

  try {
    const res = await resetContactMfa(pendingResetRow.webUserId);

   
    const data = res?.data;

    const isSuccess =
      data?.success ?? data?.isSuccess ?? (res?.status === 200);

    const message =
      data?.message ||
      (isSuccess
        ? `MFA Key reset Successfully `
        : "Failed to reset MFA");

    if (isSuccess) {
      toast.success(message);
    } else {
      toast.error(message);
    }

  } catch (error) {
    console.error("Reset MFA Error:", error);

    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      "Something went wrong while resetting MFA";

    toast.error(errorMessage);
  } finally {
    setShowResetMfaConfirm(false);
    setPendingResetRow(null);
  }
};

const handleCancelResetMfa = () => {
  setShowResetMfaConfirm(false);
  setPendingResetRow(null);
};
  const handleEditContact = (row) => {
    if (!row?.webUserId) return;
    setSelectedAdminContact(row);
    const webUserId = row.webUserId;
    const contactName = row.webUserName;
    localStorage.setItem("CN",contactName);
    const webUserIdEncrypted = btoa(webUserId)
    navigate(`/AdminContact/${webUserIdEncrypted}`, {
      state: { webUserIdEncrypted },
    });
  };


  const columnDefs = [
    { headerName: "Status", field: "webStatusName" },
    {
      headerName: "MFA",
      field: "isDuoActivated",
      cellRenderer: yesNoFormatter,
    },
    { headerName: "Category", field: "categoryName" },
    {
      headerName: "Name",
      field: "webUserName",
      cellRenderer: nameCellRenderer,
    },
    { headerName: "Email", field: "email" },
    { headerName: "Company Name", field: "companyName" },
    {
      headerName: "Created",
      field: "createdDate",
      valueFormatter: (params) => formatDateOnly(params.value),
    },
    {
      headerName: "Last Login",
      field: "lastLoginDate",
      valueFormatter: (params) => formatDateTime(params.value),
    },
    {
      headerName: "Last Updated",
      field: "lastUpdateDate",
      valueFormatter: (params) => formatDateOnly(params.value),
    },
    {
      headerName: "Client Commentary",
      field: "isManagerCommentary",
      cellRenderer: yesNoFormatter,
    },
    {
      headerName: "Manager Commentary",
      field: "isManagerCommentary",
      cellRenderer: yesNoFormatter,
    },
    {
      headerName: "Email Distribution",
      field: "isEmailDistribution",
      cellRenderer: yesNoFormatter,
    },
    {
      headerName: "Paper Distribution",
      field: "isPaperDistribution",
      cellRenderer: yesNoFormatter,
    },
    {
      headerName: "Billing Invoice Notification",
      field: "isBillingInvoiceNotification",
      cellRenderer: yesNoFormatter,
    },
    {
      headerName: "Tax Documents Notification",
      field: "isTaxDocumentsNotification",
      cellRenderer: yesNoFormatter,
    },
    {
      headerName: "Edit",
      field: "editAction",
      alwaysVisible: true,
      cellRenderer: (params) => (
        <img
          src={viewClientWebsiteIcon}
          alt="Edit"
          title="View Details"
          style={{ cursor: "pointer" }}
          onClick={() => handleEditContact(params.data)}
        />
      ),
    },
    {
      headerName: "Reset MFA",
      field: "resetMfaAction",
      alwaysVisible: true,
      cellRenderer: (params) => (
        <img
          src={resetMfaIcon}
          alt="Reset MFA"
          title="Reset MFA Key"
          style={{ cursor: "pointer" }}
          onClick={() => handleResetMfaClick(params.data)}
        />
      ),
    },
  ];

  const mapLabelToField = (name) => {
    const n = (name || "").trim().toLowerCase();

    if (n === "status") return "webStatusName";
    if (n === "duo" || n === "mfa") return "isDuoActivated";
    if (n === "category") return "categoryName";
    if (n === "company name") return "companyName";
    if (n === "name") return "webUserName";
    if (n === "email") return "email";
    if (n === "created") return "createdDate";
    if (n === "last login") return "lastLoginDate";
    if (n === "last updated" || n === "last update")
      return "lastUpdateDate";
    if (n === "client commentary" || n === "manager commentary")
      return "isManagerCommentary";
    if (n === "email distribution") return "isEmailDistribution";
    if (n === "paper distribution") return "isPaperDistribution";
    if (n === "billing invoice notification")
      return "isBillingInvoiceNotification";
    if (n === "tax documents notification")
      return "isTaxDocumentsNotification";
    if (n === "edit") return "editAction";
    if (n === "reset mfa") return "resetMfaAction";

    return null;
  };

  const getDefaultCustomizeRows = () =>
    columnDefs.map((c, i) => ({
      columnName: c.headerName,
      isVisible: true,
      order: i,
    }));

  const visibleColumnDefs = useMemo(() => {
    if (!visibleCols.size) return columnDefs;
    return columnDefs.filter(
      (c) => c.alwaysVisible || visibleCols.has(c.field)
    );
  }, [columnDefs, visibleCols]);

  const gridKey = useMemo(() => {
    if (!visibleColumnDefs.length) return "default";
    return visibleColumnDefs.map((c) => c.field).join("|");
  }, [visibleColumnDefs]);

  const handleExport = () => {
    if (!rowData || rowData.length === 0) return;

    const headers = [
      "webStatusName",
      "isDuoActivated",
      "categoryName",
      "webUserName",
      "email",
      "createdDate",
      "lastLoginDate",
      "lastUpdateDate",
      "isManagerCommentary",
      "isEmailDistribution",
      "isPaperDistribution",
    ];

    const formattedRows = rowData.map((r) => ({
      webStatusName: r.webStatusName,
      isDuoActivated: r.isDuoActivated ? "Yes" : "No",
      categoryName: r.categoryName,
      webUserName: r.webUserName,
      email: r.email,
      createdDate: formatDateOnly(r.createdDate),
      lastLoginDate: formatDateTime(r.lastLoginDate),
      lastUpdateDate: formatDateOnly(r.lastUpdateDate),
      isManagerCommentary: r.isManagerCommentary ? "Yes" : "No",
      isEmailDistribution: r.isEmailDistribution ? "Yes" : "No",
      isPaperDistribution: r.isPaperDistribution ? "Yes" : "No",
    }));

    exportToExcel({
      fileName: "AdminContacts.xlsx",
      headers,
      rows: formattedRows,
    });
  };

  const loadSavedCustomize = async () => {
    if (!customizeUserName) return;

    const res = await getContactCustomize(PAGE_ID, userId, customizeUserName);
    const dto = res?.data || {};

    const cols = dto?.columnsSettings || dto?.ColumnsSettings || [];
    const qid = Number(dto?.gridQueryID || dto?.GridQueryID || 0);

    setGridQueryId(qid);

    if (!cols.length) {
      const defaults = getDefaultCustomizeRows();
      setCustomizeRows(defaults);
      setVisibleCols(new Set(columnDefs.map((c) => c.field)));
      return;
    }

    const normalizedRows = cols
      .map((c, i) => ({
        columnName: c.columnName || c.ColumnName,
        isVisible: !!(c.isVisible ?? c.IsVisible),
        order: Number(c.order ?? c.Order ?? i),
      }))
      .sort((a, b) => a.order - b.order);

    setCustomizeRows(normalizedRows);

    const nextVisible = new Set();
    normalizedRows.forEach((r) => {
      if (r.isVisible) {
        const field = mapLabelToField(r.columnName);
        if (field) nextVisible.add(field);
      }
    });

    setVisibleCols(nextVisible);
  };

  useEffect(() => {
    loadSavedCustomize();
  }, [userId, customizeUserName]);

  const onOpenCustomize = async () => {
    await loadSavedCustomize();
    setShowCustomize(true);
  };

  const onSaveCustomize = async () => {
    const effectiveQid = gridQueryId || 1234;

    const payload = {
      ColumnsSettings: customizeRows.map((r, i) => ({
        ColumnName: r.columnName,
        IsVisible: !!r.isVisible,
        SortOrder: i,
        GroupIndex: effectiveQid,
      })),
      GridID: 123,
      GridQueryID: effectiveQid,
      IsDefault: true,
      IsShared: true,
      PageID: PAGE_ID,
      QueryName: customizeUserName,
      UserID: userId,
    };

    await saveContactCustomize(payload);
    await loadSavedCustomize();
    setShowCustomize(false);
  };

  const customizeColumnDefs = useMemo(
    () => [
      {
        headerName: "",
        field: "isVisible",
        width: 90,
        headerComponent: () => {
          const allChecked =
            customizeRows.length > 0 &&
            customizeRows.every((r) => r.isVisible);

          return (
            <input
              type="checkbox"
              checked={allChecked}
              onChange={(e) => {
                const checked = e.target.checked;

                setCustomizeRows((prev) =>
                  prev.map((row) => ({
                    ...row,
                    isVisible: checked,
                  }))
                );
              }}
            />
          );
        },
        cellRenderer: (params) => (
          <input
            type="checkbox"
            checked={!!params.value}
            onChange={(e) => {
              const checked = e.target.checked;

              setCustomizeRows((prev) =>
                prev.map((row, i) =>
                  i === params.node.rowIndex
                    ? { ...row, isVisible: checked }
                    : row
                )
              );
            }}
          />
        ),
      },
      {
        headerName: "Column Name",
        field: "columnName",
        flex: 1,
      },
    ],
    [customizeRows]
  );

  return (
    <div style={{ marginTop: "10px" }}>
      <div
        style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}
      >
        <img
          src={settingsIcon}
          alt="Customize"
          style={{ width: 26, cursor: "pointer", marginRight: 10 }}
          onClick={onOpenCustomize}
        />
        <img
          src={excelIcon}
          alt="Export"
          style={{ width: 26, cursor: "pointer" }}
          onClick={handleExport}
        />
      </div>

      <div className="ag-theme-alpine" style={{ height: 500, width: "100%" }}>
        <AgGridReact
          key={gridKey}
          theme={"legacy"}
          rowData={rowData}
          columnDefs={visibleColumnDefs}
          pagination={true}
          loading={loading}
          defaultColDef={{
            flex: 1,
            minWidth: 100,
            resizable: true,
          }}
        />
      </div>

      <AESClientWebsiteDrawer
        open={showCustomize}
        setOpen={setShowCustomize}
        title="Customize View Settings"
        size="sm"
      >
        <div className="ag-theme-alpine" style={{ height: 600 }}>
          <AgGridReact
            theme={"legacy"}
            rowData={customizeRows}
            columnDefs={customizeColumnDefs}
            suppressScrollOnNewData={true}
            defaultColDef={{
              sortable: false,
              filter: false,
              resizable: true,
              flex: 1,
              minWidth: 100,
   
            }}
          />
        </div>

        <div style={{ marginTop: 15, textAlign: "right" }}>
          <button
            className="btn btn-default"
            onClick={() => setShowCustomize(false)}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            style={{ marginLeft: 10 }}
            onClick={onSaveCustomize}
          >
            Save
          </button>
        </div>
      </AESClientWebsiteDrawer>

      <ConfirmDialog
        open={showResetMfaConfirm}
        title="Do you want to Reset the MFA?"
        message="The MFA key for this contact will be reset. Are you sure?"
        onConfirm={handleConfirmResetMfa}
        onCancel={handleCancelResetMfa}
      />
    </div>
  );
};

export default ContactGrid;
